import { useEffect } from 'react';
import '@rio-cloud/iframe-resizer/child';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';

import { ErrorBoundary } from '#components/error/ErrorBoundary.tsx';
import ErrorFallback from '#components/error/ErrorFallback.tsx';
import { ensureUserIsLoggedIn } from '#configuration/login';
import { TRACKING_CATEGORIES } from '#configuration/setup/googleAnalytics.ts';
import { widgetRouter } from '#routes/WidgetRouter.tsx';

import { config } from './config';
import { getUserSessionHooks } from './configuration';
import { DEFAULT_LOCALE, extractLanguage } from './configuration/lang/lang';
import { useDisplayMessages, useLocale } from './configuration/lang/langSlice';
import { store } from './configuration/setup/store';
import { appRouter } from './routes/AppRouter';
import { routes } from './routes/routes';

const normalizeHashRoute = () => {
    const { hash, pathname, search } = window.location;

    if (hash !== '') {
        return;
    }

    if (![routes.TOURSERVICE, routes.LIVEMONITOR].some(route => pathname.startsWith(route))) {
        return;
    }

    const normalizedUrl = new URL(window.location.href);
    normalizedUrl.pathname = '/';
    normalizedUrl.search = search;
    normalizedUrl.hash = pathname;

    window.history.replaceState({}, '', normalizedUrl);
};

const App = () => {
    const isWidgetRoute = window.location.hash.includes(routes.LIVEMONITOR);
    const router = isWidgetRoute ? widgetRouter : appRouter;
    const userLocale = useLocale();
    const displayMessages = useDisplayMessages();

    // We want the `<html lang>` attribute to be in sync with the language currently displayed
    useEffect(() => {
        const language = extractLanguage(userLocale);
        const htmlElement = document.documentElement;
        if (language && htmlElement.getAttribute('lang') !== language) {
            htmlElement.setAttribute('lang', language);
        }
    }, [userLocale]);

    useEffect(() => {
        document.documentElement.classList.toggle('is-frame-widget', isWidgetRoute);
    }, [isWidgetRoute]);

    if (!(displayMessages && userLocale)) {
        return null;
    }

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ErrorBoundary fallback={<ErrorFallback trackingCategory={TRACKING_CATEGORIES.ORDER_COMMUNICATION_WEB} />}>
                <RouterProvider router={router} />
            </ErrorBoundary>
        </IntlProvider>
    );
};

const renderApplication = () => {
    createRoot(document.getElementById('root') as HTMLElement).render(
        <Provider store={store}>
            <App />
        </Provider>
    );
};

const isDev = import.meta.env.DEV;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;
normalizeHashRoute();
if ((isDev && config.enableMockServer) || isProdPreview) {
    await import('../mocks/browser').then(({ startWorker }) => startWorker());
}

await ensureUserIsLoggedIn(renderApplication, getUserSessionHooks());
